import React, { useCallback, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import axios from 'axios';

// Component imports
import Sidebar from '../../components/SideBar/Sidebar';
import Input from '../../components/Inputs/Input/Input';
import Button from '../../components/Buttons/Button/Button';
import Loading from '../../components/Loading/Loading';
import DeleteUser from './Modals/DeleteUser';

// Style and icon imports
import * as Body from './Styles';
import * as defines from '../../../utils/defines/Defines';
import * as Functions from '../../../utils/functions/Functions';
import { State } from '../../redux/store';

// Icons
import Avatar from '../../../assets/images/avatar.png';
import UsuarioIcon from '../../../assets/icons/usuario-icon.svg';
import TecnicoIcon from '../../../assets/icons/tecnico-icon.svg';
import CheckIcon from '../../../assets/icons/check-icon-white.svg';

// Types
interface UsuarioProps {
  idEmpresarial: string;
  idUsuario: string;
  fotoUsuario: string | null;
  ddd: string;
  celular: string;
  nomeCompleto: string;
  email: string;
  cargo: number;
  encarregadoDados: number;
}

const EditDefaultUser: React.FC = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { state } = useLocation();
  const { user } = state as { user: UsuarioProps };

  // Redux selectors
  const userType = useSelector((state: State) => state.userType.userType);
  const selectedUnity = useSelector((state: State) => state.selectedUnity.selectedUnity);

  // Component state
  const [modalDelete, setModalDelete] = useState(false);
  const [loading, setLoading] = useState(false);
  const [nivelAcesso, setNivelAcesso] = useState(user.cargo);
  const [isEncarregadoDados, setIsEncarregadoDados] = useState(user.encarregadoDados === 1);
  const [errorEmail, setErrorEmail] = useState(false);

  // Memoized cargo mapping
  const cargoMap = useMemo(() => ({
    [defines.CargoEmpresarial['Administrador']]: 'Administrador',
    [defines.CargoEmpresarial['Padrão']]: 'Padrão'
  }), []);

  // Memoized user details
  const userDetails = useMemo(() => ({
    nomeCompleto: user.nomeCompleto,
    email: user.email,
    ddd: user.ddd,
    celular: user.celular,
    imagemUsuario: user.fotoUsuario || ''
  }), [user]);

  // Email validation handler
  const handleEmailChange = useCallback((value: string) => {
    const isValidEmail = Functions.verifyEmail(value);
    setErrorEmail(!isValidEmail);
  }, []);

  // Update user role and data handler
  const updateUser = useCallback(() => {
    setLoading(true);
    axios.put(
      `${defines.apiURL}/api/EmpresarialCRUD?userTypeRequest=${userType}&idFilial=${Functions.ReplaceSpecialCharacters(selectedUnity)}`,
      {
        idEmpresarial: user.idEmpresarial,
        encarregadoDados: isEncarregadoDados ? 1 : 0,
        nivelAcesso: nivelAcesso,
      },
      { withCredentials: true }
    )
    .then(() => {
      setLoading(false);
      dispatch({
        type: 'ADD_NOTIFICATION',
        status: 1,
        title: '',
        text: 'Cargo alterado com sucesso!',
      });
    })
    .catch((error) => {
      setLoading(false);
      Functions.ResolveErrors(
        error.response.status,
        error.response.data.code,
        error.response.data.message,
        navigate,
        dispatch,
        error.response.data.content
      );
    });
  }, [
    userType, 
    selectedUnity, 
    user.idEmpresarial, 
    isEncarregadoDados, 
    nivelAcesso, 
    navigate, 
    dispatch
  ]);

  // Render user role selection card
  const renderRoleCard = (role: number, icon: string, title: string) => (
    <Body.Card
      onClick={() => setNivelAcesso(role)}
      style={{
        border: nivelAcesso === role 
          ? `2px solid ${defines.BlueTec}` 
          : 'none',
        transition: 'all 0.3s ease',
      }}
    >
      <Body.CardIcon src={icon} />
      <Body.CardTitle>{title}</Body.CardTitle>
    </Body.Card>
  );

  return (
    <Body.Wrapper>
      <Sidebar
        type={userType}
        content={defines.ContentSidebar.MANAGE_COMPANY}
        tituloPagina="Editar Usuário Empresarial"
        backAction={() => navigate('/usuarios-empresarial')}
      />
      <Body.Container>
        {/* User Photo */}
        <div 
          style={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            minHeight: '300px',
          }}
        >
          <div style={{ position: 'relative', display: 'inline-block' }}>
            <Body.Photo src={userDetails.imagemUsuario || Avatar} />
          </div>
        </div>

        {/* User Details Inputs */}
        <Body.Row>
          <Input
            text={userDetails.nomeCompleto}
            setText={() => {}}
            placeholder="Nome Completo"
            title="Nome Completo"
            disabled
          />
        </Body.Row>
        <Body.Row>
          <Input
            text={userDetails.email}
            setText={handleEmailChange}
            placeholder="Email"
            title="Email"
            disabled
          />
        </Body.Row>
        <Body.Row>
          <Input
            width={'20%'}
            text={userDetails.ddd}
            setText={() => {}}
            placeholder="DDD"
            title="DDD"
            limit={3}
            disabled
          />
          <Input
            width={'80%'}
            text={userDetails.celular}
            setText={() => {}}
            placeholder="Telefone"
            title="Telefone"
            limit={9}
            disabled
          />
        </Body.Row>

        {/* Email Error */}
        {errorEmail && (
          <Body.TextError>
            Formato de email inválido ex: email@email.com
          </Body.TextError>
        )}

        {/* Role Selection */}
        <div 
          style={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            minHeight: '120px',
          }}
        >
          <Body.Row>
            {renderRoleCard(
              defines.CargoEmpresarial['Padrão'], 
              UsuarioIcon, 
              'Padrão'
            )}
            {renderRoleCard(
              defines.CargoEmpresarial['Administrador'], 
              TecnicoIcon, 
              'Administrador'
            )}
          </Body.Row>
        </div>

        {/* Data Manager Toggle */}
        <div
          style={{
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'center',
            padding: '20px 0',
          }}
          onClick={() => setIsEncarregadoDados(!isEncarregadoDados)}
        >
          <Body.ToggleBtn active={isEncarregadoDados}>
            {isEncarregadoDados && <Body.IconCheckbox src={CheckIcon} />}
          </Body.ToggleBtn>
          <Body.Text style={{ width: 'calc(100% - 40px)' }}>
            Encarregado de Dados
          </Body.Text>
        </div>

        {/* Action Buttons */}
        <Body.Row>
          <Button
            text="Salvar"
            color="blue"
            action={updateUser}
          />
          <Button
            text="Desvincular Usuário"
            color="yellow"
            action={() => setModalDelete(true)}
          />
        </Body.Row>

        {/* Loading and Delete Modal */}
        {loading && <Loading />}
        {modalDelete && (
          <DeleteUser
            toggle={setModalDelete}
            usuario={user}
            nomeFilial={'atual'}
            idFilial={selectedUnity}
            regenerate={() => {}}
          />
        )}
      </Body.Container>
    </Body.Wrapper>
  );
};

export default EditDefaultUser;